import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslations } from 'react-utilities';
import { httpService } from 'core-utilities';
import { eventStreamService } from 'core-roblox-utilities';
import { translationConfig } from '../translation.config';
import SearchInput from '../components/SearchInput';
import layout from '../constants/layoutConstants';
import search from '../constants/searchConstants';
import searchService, { GamesAutocompleteSuggestionEntryType } from '../services/searchService';
import events from '../constants/searchEventStreamConstants';
import navigationUtil from '../util/navigationUtil';
import searchUtil from '../util/searchUtil';
import useDebounce from '../hooks/useDebounce';
import usePrevious from '../hooks/usePrevious';

export function UniversalSearch({ translate, isUniverseSearchShown }) {
  const [searchInput, setSearchInput] = useState(
    navigationUtil.parseQuery(window.location.search).keyword || ''
  );

  const debouncedSearchInput = useDebounce(searchInput, search.debounceTimeout);
  const previousDebouncedSearchInput = usePrevious(debouncedSearchInput);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState(null);
  const [autocompleteSessionInfo, setAutocompleteSessionInfo] = useState(
    events.generateSessionInfo()
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuHover, setIsMenuHover] = useState(false);
  const [indexOfSelectedOption, setSelectedListOptions] = useState(0);
  const { keyCodes } = layout;

  const universalSearchLinks = search.isAutocompleteSuggestionsIXPTestEnabled()
    ? navigationUtil.getNewUniversalSearchLinks()
    : navigationUtil.getUniversalSearchLinks();
  const gameSearchLinkIndex = universalSearchLinks.findIndex(
    ({ label }) => label === 'Label.sGames'
  );
  const searchSuggestions = useMemo(() => {
    if (autocompleteSuggestions) {
      if (search.isSpecialTreatmentAutocompleteRestricted()) {
        return universalSearchLinks;
      }
      if (search.isSpecialTreatment()) {
        const splicedSuggestions = autocompleteSuggestions
          .filter(
            suggestion =>
              suggestion.searchQuery !== debouncedSearchInput.toLowerCase() ||
              suggestion.type === GamesAutocompleteSuggestionEntryType.GameSuggestion
          )
          .slice(0, search.numberOfSpecialTreatmentAutocompleteSuggestions);
        return [
          ...universalSearchLinks.slice(0, gameSearchLinkIndex + 1),
          ...splicedSuggestions,
          ...universalSearchLinks.slice(gameSearchLinkIndex + 1)
        ];
      }
      const splicedSuggestions = autocompleteSuggestions
        .filter(
          suggestion =>
            suggestion.searchQuery !== debouncedSearchInput.toLowerCase() ||
            suggestion.type === GamesAutocompleteSuggestionEntryType.GameSuggestion
        )
        .slice(0, search.numberOfAutocompleteSuggestions());
      return [
        ...universalSearchLinks.slice(0, gameSearchLinkIndex + 1),
        ...splicedSuggestions,
        ...universalSearchLinks.slice(gameSearchLinkIndex + 1)
      ];
    }
    return universalSearchLinks;
  }, [autocompleteSuggestions]);
  const searchSuggestionLength = searchSuggestions.length;

  useEffect(() => {
    eventStreamService.sendEvent(
      events.search(searchInput, events.actionTypes.submit, autocompleteSessionInfo)
    );
    const getAutocompleteSuggestion = async () => {
      if (search.isAutocompleteSuggestionsIXPTestEnabled() && debouncedSearchInput !== '') {
        setAutocompleteSuggestions(null);
        const start = Date.now();
        try {
          const data = await searchService.postRequestSuggestion(debouncedSearchInput);
          const end = Date.now();
          eventStreamService.sendEvent(
            events.searchAutocomplete(
              debouncedSearchInput,
              previousDebouncedSearchInput,
              false,
              searchUtil.serializeSuggestions(data.entries, debouncedSearchInput),
              data.algorithmName,
              end - start,
              search.debounceTimeout,
              autocompleteSessionInfo
            )
          );
          setAutocompleteSuggestions(data.entries);
        } catch (error) {
          if (!httpService.isCancelled(error)) {
            setAutocompleteSuggestions([]);
          }
        }
      }
    };
    getAutocompleteSuggestion();
  }, [debouncedSearchInput]);

  const resetAutocompleteSessionInfo = () => {
    setAutocompleteSessionInfo(events.generateSessionInfo());
  };

  const handleSearch = ({ target: { value } }) => {
    if (value.length < searchInput.length) {
      eventStreamService.sendEvent(
        events.searchTextTrim(searchInput, value, undefined, autocompleteSessionInfo)
      );
    }

    if (value.length === 0) {
      resetAutocompleteSessionInfo();
    }

    setSelectedListOptions(0);
    setIsMenuOpen(value.length > 0);
    setSearchInput(value);
  };

  const openMenu = () => {
    const newAutoCompleteSessionInfo = events.generateSessionInfo();
    eventStreamService.sendEvent(
      events.search(searchInput, events.actionTypes.open, newAutoCompleteSessionInfo)
    );
    setAutocompleteSessionInfo(newAutoCompleteSessionInfo);

    if (searchInput.length === 0 || isMenuHover) return;
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    eventStreamService.sendEvent(
      events.search(searchInput, events.actionTypes.close, autocompleteSessionInfo)
    );

    setIsMenuOpen(false);
  };

  const onSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onKeyDown = e => {
    let currentCursor = indexOfSelectedOption;
    if (
      e.keyCode === keyCodes.arrowUp ||
      e.keyCode === keyCodes.arrowDown ||
      e.keyCode === keyCodes.tab
    ) {
      e.stopPropagation();
      e.preventDefault();
      if (e.keyCode === keyCodes.arrowUp) {
        currentCursor -= 1;
      } else {
        currentCursor += 1;
      }

      currentCursor %= searchSuggestionLength;
      if (currentCursor < 0) {
        currentCursor = searchSuggestionLength + currentCursor;
      }
      setSelectedListOptions(currentCursor);
    }
  };

  const onKeyUp = e => {
    if (e.keyCode === keyCodes.enter) {
      e.stopPropagation();
      e.preventDefault();

      const suggestion = searchSuggestions[indexOfSelectedOption];

      if (searchUtil.isAutocompleteSuggestion(suggestion)) {
        eventStreamService.sendEvent(
          events.searchSuggestionClicked(
            debouncedSearchInput,
            undefined,
            indexOfSelectedOption,
            suggestion.searchQuery,
            searchUtil.getAutocompleteSearchType(suggestion),
            searchUtil.serializeSuggestions(searchSuggestions, searchInput),
            autocompleteSessionInfo
          )
        );
      } else {
        eventStreamService.sendEvent(
          events.searchSuggestionClicked(
            debouncedSearchInput,
            undefined,
            indexOfSelectedOption,
            debouncedSearchInput,
            searchUtil.getDefaultSearchType(suggestion),
            searchUtil.serializeSuggestions(searchSuggestions, searchInput),
            autocompleteSessionInfo
          )
        );
      }
      resetAutocompleteSessionInfo();
      const suggestionUrl = searchUtil.getSuggestionUrl(suggestion, e);
      if (suggestionUrl) {
        window.location = suggestionUrl;
      }
    }
  };

  return (
    <SearchInput
      {...{
        searchInput,
        handleSearch,
        openMenu,
        closeMenu,
        setIsMenuHover,
        isMenuOpen,
        indexOfSelectedOption,
        onSubmit,
        onKeyDown,
        onKeyUp,
        isUniverseSearchShown,
        translate,
        searchSuggestions,
        autocompleteSessionInfo,
        resetAutocompleteSessionInfo
      }}
    />
  );
}

UniversalSearch.defaultProps = {
  isUniverseSearchShown: true
};

UniversalSearch.propTypes = {
  translate: PropTypes.func.isRequired,
  isUniverseSearchShown: PropTypes.bool
};

export const UniversalSearchContainer = withTranslations(UniversalSearch, translationConfig);
